import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadEPCRatingsDetails } from '../../../store/directusService';
import { clearEPCRatings, loadParticipantEPCRatings } from '../../../store/participantService';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import CreateEPCRating from './CreateEPCRating';
import EditEPCRating from './EditEPCRating';
import EPCRatingTableRow from './EPCRatingTableRow';

import app from '../../../app.module.css';
import local from '../barriers/barriers.module.css';
import classes from './environment.module.css';

const headCells = [
    { id: 'dateAdded', numeric: false, label: 'Date Added' },
    { id: 'epcRating', numeric: false, label: 'EPC Rating' }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'dateAdded',
    page: 0,
    rowsPerPage: 100
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const editRoles = [QUALITY, SUPERUSER];

const Environment = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [openEditor, setOpenEditor] = useState(false);
    const [formType, setFormType] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isEditDisabled, setIsEditDisabled] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const epcRatingsDetails = useSelector(
        (state) => state.entities.directusService.epcRatingsDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const participantEPCRatings = useSelector(
        (state) => state.entities.participantService.participantEPCRatings
    );

    // USE EFFECTS
    useEffect(() => {
        setRows([]);
        epcRatingsDetails.length < 1 && dispatch(loadEPCRatingsDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!currentParticipant?.id) return;
        const isDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || isDisabledOnClosedStatus);
        const isEditDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
        setIsEditDisabled(!hasRole(editRoles, roles) || isEditDisabledOnClosedStatus);
    }, [currentParticipant.id]);

    useEffect(() => {
        if (!('id' in currentParticipant)) return;
        dispatch(loadParticipantEPCRatings(currentParticipant.id));
    }, [currentParticipant]);

    useEffect(() => {
        setRows(participantEPCRatings);
    }, [participantEPCRatings]);

    // HELPER fNS
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <EPCRatingTableRow
                key={el.id}
                row={el}
                isEditDisabled={isEditDisabled}
                onEdit={onEditEnvironment}
            />
        ));

    const cleanUp = () => dispatch(clearEPCRatings());

    // EVENT HANDLERS
    const onCreateEnvironment = () => {
        setFormType('create');
        setOpenEditor(true);
    };

    const onEditEnvironment = (row) => {
        setSelectedRow(row);
        setFormType('edit');
        setOpenEditor(true);
    };

    const onEditorClose = () => setOpenEditor(false);

    // RENDER
    return (
        <div className={classes.environmentWrapper}>
            <div className={local.tableWrapper}>
                <h2 className={app.sectionHeading}>Your environment details</h2>
                {rows.length < 1 ? (
                    <LoadingSpinner content="No EPC ratings found" />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>

            <div>
                {!openEditor && (
                    <Button
                        id="environmentCreateButton"
                        disabled={isDisabled}
                        content="Add EPC Rating"
                        onClick={onCreateEnvironment}
                        icon={true}
                    />
                )}
                {openEditor && formType === 'create' && (
                    <CreateEPCRating onClose={onEditorClose} disabled={isDisabled} />
                )}
                {openEditor && formType === 'edit' && (
                    <EditEPCRating
                        onClose={onEditorClose}
                        isEditDisabled={isEditDisabled}
                        row={selectedRow}
                    />
                )}
            </div>
        </div>
    );
};

export default Environment;
